import React, { useState } from 'react';
import { GraduationCap, Truck, MapPin, Clock, CheckCircle2, Phone, Mail, ArrowRight, Globe, Shield, DollarSign, Luggage } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const StudentShippingCanadaToIndiaPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', weight: '' });
  const { toast } = useToast();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ title: "Quote Request Received!", description: "Our team will contact you within 24 hours with student shipping rates to India." });
    setFormData({ name: '', email: '', phone: '', weight: '' });
  };

  const benefits = [
    { icon: DollarSign, title: "Student-Friendly Rates", description: "Special discounted rates for students shipping belongings to India. Save money compared to airline baggage fees." },
    { icon: Luggage, title: "Ship Multiple Bags", description: "Send suitcases, boxes, and personal belongings. Perfect for summer breaks, semester ends, and post-graduation returns." },
    { icon: Truck, title: "Flexible Pickup", description: "Convenient pickup from your dorm, apartment, or university campus across Canada." },
    { icon: Globe, title: "Reliable Delivery", description: "Partner with DHL, FedEx, and UPS for safe delivery to your family's address anywhere in India." },
    { icon: Shield, title: "Simple Customs", description: "We help with customs documentation for student shipments and personal belongings." },
    { icon: Clock, title: "Flexible Timing", description: "Economy and express options available. Ship when it suits your schedule and budget." }
  ];

  const faqs = [
    { question: "How much does student shipping from Canada to India cost?", answer: "Student shipping costs depend on weight and dimensions. For bulk shipments over 100kg, rates start at $7.99/kg (airport-to-airport) or $8.49/kg (doorstep delivery, 138kg min). Individual suitcases typically range from $100-$200. We offer special student discounts and compare rates from multiple carriers to find the best price." },
    { question: "Can I ship my belongings home to India after graduation?", answer: "Absolutely! Many international students use our service to ship belongings back to India after completing their studies in Canada. We handle everything from pickup to delivery, including customs clearance." },
    { question: "What can I ship as a student?", answer: "You can ship clothing, textbooks, electronics, personal care items, kitchenware, bedding, shoes, and other personal belongings. We cannot ship prohibited items like liquids, flammables, or perishable foods." },
    { question: "How long does student shipping to India take?", answer: "Express shipping takes 5-10 business days, economy shipping takes 15-25 days. Many students choose economy options during summer breaks when timing is flexible, saving significant money." },
    { question: "Do you pickup from university campuses?", answer: "Yes! We provide pickup services from university campuses, student housing, and apartments across Canada including Toronto, Brampton, Vancouver, Surrey, and other cities." },
    { question: "Is shipping cheaper than paying airline baggage fees?", answer: "Yes, in most cases! Airlines charge $100-$300+ per extra bag. Shipping your belongings separately with Shippzy is often more economical, especially for multiple bags or heavy items." },
    { question: "Do I need to pay customs duty on student shipments?", answer: "Personal belongings and used items typically have lower or no customs duties. New items may incur duty. We help you prepare proper documentation to minimize customs charges." },
    { question: "Can I track my student shipment to India?", answer: "Yes! All shipments include real-time tracking. You'll receive a tracking number via email and can monitor your belongings from Canada to India." }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>Student Shipping Canada to India | Luggage & Belongings – Shippzy</title>
        <meta name="description" content="Student shipping from Canada to India. Affordable rates for international students. Ship suitcases, boxes, belongings home after semester or graduation. Door-to-door delivery with tracking." />
        <meta name="keywords" content="student shipping Canada to India, international student luggage shipping, ship belongings to India, student relocation India, send luggage home India, post-graduation shipping India" />
        <link rel="canonical" href="https://www.shippzy.ca/student-shipping-canada-to-india" />
        <meta property="og:title" content="Student Shipping Canada to India | Shippzy" />
        <meta property="og:description" content="Affordable student shipping from Canada to India. Special rates for international students. Door-to-door service with customs assistance." />
        <meta property="og:url" content="https://www.shippzy.ca/student-shipping-canada-to-india" />
      </Helmet>

      <div className="min-h-screen bg-white">
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                🎓 Student-Friendly Shipping to India
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">Student Shipping from Canada to India</h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Shippzy provides affordable and reliable shipping solutions for international students in Canada. Whether you're heading home for summer break, completing your studies, or relocating after graduation, we help you ship your belongings, suitcases, textbooks, and personal items back to India at student-friendly rates. Convenient campus pickup, customs assistance, and door-to-door delivery to your family in India.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg" onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}>Get Student Quote</Button>
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg" onClick={() => window.location.href = 'tel:+14376160758'}>Call: +1 (437) 616-0758</Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Students Choose Shippzy</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">Affordable, reliable, and student-focused shipping from Canada to India</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Perfect For International Students</h2>
              <p className="text-xl text-gray-600">Common student shipping scenarios we handle</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: 'Summer Break Shipping', desc: 'Ship your belongings home during summer and avoid storage fees in Canada' },
                { title: 'Post-Graduation Return', desc: 'Send all your possessions back to India after completing your degree' },
                { title: 'Semester Exchange', desc: 'Ship items between Canada and India for exchange programs' },
                { title: 'Excess Baggage', desc: 'Avoid airline fees by shipping extra suitcases and boxes separately' }
              ].map((item, i) => (
                <Card key={i} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <CheckCircle2 className="w-8 h-8 text-green-500 mb-3" />
                    <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Everything students need to know about shipping to India</p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <ArrowRight className={`w-5 h-5 text-red-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {openFaq === index && <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="quote-form" className="py-20 bg-gradient-to-r from-red-600 to-yellow-500">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white mb-8">
              <h2 className="text-4xl font-bold mb-4">Ready to Ship Your Belongings to India?</h2>
              <p className="text-xl">Get your free student shipping quote today</p>
            </div>
            <Card className="shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Get Your Free Student Quote</CardTitle>
                <CardDescription className="text-center">Special rates for international students</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label><Input name="name" value={formData.name} onChange={handleChange} required placeholder="Your name" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label><Input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label><Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+1 (123) 456-7890" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Estimated Weight (kg)</label><Input type="number" name="weight" value={formData.weight} onChange={handleChange} placeholder="50" /></div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white h-12 text-lg">Get Quote Now</Button>
                </form>
              </CardContent>
            </Card>
            <div className="mt-8 text-center text-white">
              <p className="text-lg mb-4">Or contact us directly:</p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <a href="tel:+14376160758" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"><Phone className="w-5 h-5" />+1 (437) 616-0758</a>
                <a href="mailto:ship@shippzy.ca" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"><Mail className="w-5 h-5" />ship@shippzy.ca</a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Trusted by International Students Across Canada</h3>
            <p className="text-gray-300 mb-6">Affordable student shipping from Canada to India with door-to-door delivery, customs support, and campus pickup. Save money compared to airline baggage fees.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-3" onClick={() => window.location.href = '/ship-suitcase-canada-to-india'}>Explore Suitcase Shipping</Button>
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3" onClick={() => window.location.href = '/relocation-shipping-canada-to-india'}>Relocation Services</Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default StudentShippingCanadaToIndiaPage;
